import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaTruck, FaClock, FaStar, FaTimes, FaArrowRight, FaFire } from 'react-icons/fa';
import { useCart } from '../context/CartContext.tsx';
import { database } from '../firebase/config.ts';
import { ref, onValue } from 'firebase/database';
import { Product, Supplement, Category, OptionGroup } from '../types/index.ts';
import './Home.css';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [selectedItem, setSelectedItem] = useState<Product | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Supplement[]>([]);
  const [supplements, setSupplements] = useState<Supplement[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuData, setMenuData] = useState<Record<string, Product[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [showStickyNav, setShowStickyNav] = useState<boolean>(false);
  const [showCartModal, setShowCartModal] = useState<boolean>(false);
  const [lastAddedItem, setLastAddedItem] = useState<Product | null>(null);
  const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([]);
  const [selectedOptionGroups, setSelectedOptionGroups] = useState<Record<string, string[]>>({});

  // Mouse parallax logic
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e: React.MouseEvent) => {
    const { clientX, clientY, currentTarget } = e;
    const { width, height, left, top } = currentTarget.getBoundingClientRect();
    const x = (clientX - left) / width - 0.5;
    const y = (clientY - top) / height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  };

  const heroImageX = useTransform(mouseX, [-0.5, 0.5], [-30, 30]);
  const heroImageY = useTransform(mouseY, [-0.5, 0.5], [-30, 30]);

  // Smooth spring physics for the parallax
  const springConfig = { damping: 25, stiffness: 150 };
  const smoothHeroX = useSpring(heroImageX, springConfig);
  const smoothHeroY = useSpring(heroImageY, springConfig);

  useEffect(() => {
    // Charger les catégories
    const categoriesRef = ref(database, 'categories');
    const unsubscribeCategories = onValue(categoriesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const categoriesArray = Object.entries(data).map(([id, category]: [string, any]) => ({
          id,
          ...category
        }));
        setCategories(categoriesArray);
      } else {
        // Catégories par défaut
        setCategories([
          { id: 'burgers', name: 'Nos Burgers', icon: '🍔' },
          { id: 'pizzas', name: 'Nos Pizzas', icon: '🍕' },
          { id: 'salades', name: 'Nos Salades', icon: '🥗' },
          { id: 'texmex', name: 'Tex-Mex', icon: '🌮' },
          { id: 'kids', name: 'Menu Kids', icon: '👶' },
          { id: 'boissons', name: 'Boissons', icon: '🥤' }
        ]);
      }
    });

    // Charger les produits
    const productsRef = ref(database, 'products');
    const unsubscribeProducts = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productsArray = Object.entries(data).map(([id, product]: [string, any]) => ({
          id,
          ...product
        }));

        // Organiser par catégorie dynamiquement
        const organized: Record<string, Product[]> = {};
        productsArray.forEach(product => {
          if (!organized[product.category]) {
            organized[product.category] = [];
          }
          organized[product.category].push(product);
        });

        setMenuData(organized);
        setLoading(false);
      }
    });

    // Charger les suppléments
    const supplementsRef = ref(database, 'supplements');
    const unsubscribeSupplements = onValue(supplementsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const supplementsArray = Object.entries(data).map(([id, supplement]: [string, any]) => ({
          id,
          ...supplement
        }));
        setSupplements(supplementsArray);
      }
    });

    // Charger les groupes d'options
    const optionGroupsRef = ref(database, 'optionGroups');
    const unsubscribeOptionGroups = onValue(optionGroupsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const optionGroupsArray = Object.entries(data).map(([id, group]: [string, any]) => ({
          id,
          ...group
        }));
        setOptionGroups(optionGroupsArray);
      }
    });

    return () => {
      unsubscribeCategories();
      unsubscribeProducts();
      unsubscribeSupplements();
      unsubscribeOptionGroups();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const menuSection = document.querySelector('.menu-section');
      if (menuSection) {
        const menuTop = (menuSection as HTMLElement).offsetTop;
        const scrollPosition = window.scrollY;
        setShowStickyNav(scrollPosition > menuTop - 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    { icon: <FaTruck />, title: 'Livraison Gratuite', desc: 'À partir de 20€ d\'achat' },
    { icon: <FaClock />, title: 'Livraison Rapide', desc: '30-45 minutes' },
    { icon: <FaStar />, title: 'Qualité Premium', desc: 'Ingrédients frais' }
  ];

  const handleAddToCart = () => {
    if (selectedItem) {
      // Vérifier que tous les groupes d'options obligatoires sont sélectionnés
      if (selectedItem.optionGroups) {
        const requiredGroups = selectedItem.optionGroups
          .map(groupId => optionGroups.find(g => g.id === groupId))
          .filter(group => group && group.required);
        
        for (const group of requiredGroups) {
          if (group && (!selectedOptionGroups[group.id] || selectedOptionGroups[group.id].length === 0)) {
            alert(`Veuillez sélectionner une option pour "${group.name}"`);
            return;
          }
        }
      }
      
      const supplementOptions = selectedOptions;
      const optionGroupOptions = getSelectedOptionGroupsForCart();
      
      const allOptions = [...supplementOptions, ...optionGroupOptions];
      
      const itemToAdd = {
        ...selectedItem,
        options: allOptions
      };
      addToCart(itemToAdd);
      setLastAddedItem(itemToAdd);
      setSelectedItem(null);
      setSelectedOptions([]);
      setSelectedOptionGroups({});
      setShowCartModal(true);
    }
  };

  const toggleOption = (option: Supplement) => {
    setSelectedOptions(prev =>
      prev.find(o => o.id === option.id)
        ? prev.filter(o => o.id !== option.id)
        : [...prev, option]
    );
  };

  const handleOptionGroupSelection = (groupId: string, optionId: string, groupType: 'single' | 'multiple') => {
    setSelectedOptionGroups(prev => {
      const currentSelections = prev[groupId] || [];
      
      if (groupType === 'single') {
        // Pour les choix uniques, remplacer la sélection
        return {
          ...prev,
          [groupId]: [optionId]
        };
      } else {
        // Pour les choix multiples, ajouter/retirer l'option
        const isSelected = currentSelections.includes(optionId);
        return {
          ...prev,
          [groupId]: isSelected 
            ? currentSelections.filter(id => id !== optionId)
            : [...currentSelections, optionId]
        };
      }
    });
  };

  const getSelectedOptionGroupsForCart = () => {
    const selectedOptions: any[] = [];
    
    Object.entries(selectedOptionGroups).forEach(([groupId, optionIds]) => {
      const group = optionGroups.find(g => g.id === groupId);
      if (group && optionIds.length > 0) {
        optionIds.forEach(optionId => {
          const option = group.options.find(opt => opt.id === optionId);
          if (option) {
            selectedOptions.push({
              id: `${groupId}_${optionId}`,
              name: `${group.name}: ${option.name}`,
              price: option.price
            });
          }
        });
      }
    });
    
    return selectedOptions;
  };

  const getTotalPrice = (): number => {
    if (!selectedItem) return 0;
    const supplementsPrice = selectedOptions.reduce((sum, opt) => sum + opt.price, 0);
    const optionGroupsPrice = getSelectedOptionGroupsForCart().reduce((sum, opt) => sum + opt.price, 0);
    return selectedItem.price + supplementsPrice + optionGroupsPrice;
  };

  const scrollToMenu = () => {
    const menuSection = document.querySelector('.menu-section');
    if (menuSection) {
      const offset = 80; // Hauteur du header
      const elementPosition = menuSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToCategory = (categoryId: string) => {
    const categorySection = document.getElementById(`category-${categoryId}`);
    if (categorySection) {
      const offset = 150; // Hauteur du header + sticky nav
      const elementPosition = categorySection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="home">
        <div className="loading-container">
          <motion.div
            className="loading-spinner"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            🍕
          </motion.div>
          <p>Chargement du menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home">
      {/* Sticky Category Navigation */}
      <AnimatePresence>
        {showStickyNav && (
          <motion.div
            className="sticky-category-nav"
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sticky-nav-container">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className="sticky-category-btn"
                  onClick={() => scrollToCategory(category.id)}
                >
                  <span className="sticky-category-icon">{category.icon}</span>
                  <span className="sticky-category-name">{category.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="hero" onMouseMove={handleMouseMove}>
        <div className="container hero-container">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div 
              className="review-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <div className="stars">
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
                <FaStar />
              </div>
              <span className="review-text">
                <strong>4.9/5</strong> sur Google
              </span>
            </motion.div>

            <div className="hero-title-wrapper">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                Le Fast-Food
              </motion.h1>
              <motion.h1 
                className="highlight-red"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
              >
                Réinventé
              </motion.h1>
            </div>

            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              Des burgers gourmets, des ingrédients frais, et
              une livraison ultra-rapide.
              <br />
              Découvrez le goût de la qualité.
            </motion.p>

            <motion.div 
              className="hero-buttons"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <motion.button
                className="btn-primary btn-hero"
                onClick={scrollToMenu}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Commander Maintenant <FaArrowRight />
              </motion.button>
              <motion.button
                className="btn-secondary btn-hero-secondary"
                onClick={() => navigate('/order')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Voir la Carte
              </motion.button>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero-image"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="hero-image-wrapper">
              <motion.div
                style={{ 
                  x: smoothHeroX, 
                  y: smoothHeroY 
                }}
                whileHover={{ 
                  scale: 1.05,
                  rotate: [0, -2, 2, -1, 0]
                }}
                transition={{ 
                  duration: 0.6,
                  ease: "easeInOut"
                }}
              >
                <img
                  src="/hero-combo.png"
                  alt="Burger and Tacos"
                  className="main-burger"
                />
              </motion.div>

              {/* Floating UI Cards */}
              <motion.div
                className="floating-card card-rating"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
              >
                <div className="icon-circle">
                  <FaStar />
                </div>
                <div className="card-content">
                  <span className="card-title">4.9</span>
                  <span className="card-subtitle">Excellent</span>
                </div>
              </motion.div>

              <motion.div
                className="floating-card card-delivery"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 }}
              >
                <div className="icon-circle">
                  <FaClock />
                </div>
                <div className="card-content">
                  <span className="card-title">30 min</span>
                  <span className="card-subtitle">Livraison</span>
                </div>
              </motion.div>

              <motion.div
                className="floating-card card-hot"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2 }}
              >
                <div className="icon-circle fire">
                  <FaFire />
                </div>
                <div className="card-content">
                  <span className="card-title">Chaud</span>
                  <span className="card-subtitle">& Savoureux</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Background Elements */}
        <div className="hero-bg-blob blob-1"></div>
        <div className="hero-bg-blob blob-2"></div>
      </section>

      <section className="menu-section">
        <div className="container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Notre Menu
          </motion.h2>

          <div className="all-categories">
            {categories.map((category) => {
              const items = menuData[category.id] || [];
              if (items.length === 0) return null;
              
              return (
                <div key={category.id} id={`category-${category.id}`} className="category-section">
                  <motion.h3
                    className="category-title"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                  >
                    {category.icon} {category.name}
                  </motion.h3>
                  <div className="menu-grid">
                    {items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        className="menu-item"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.05 }}
                        whileHover={{ y: -10 }}
                      >
                        <div className="item-image">
                          <img src={item.image} alt={item.name} loading="lazy" />
                        </div>
                        <div className="item-content">
                          <h3>{item.name}</h3>
                          <p className="item-description">{item.description}</p>
                          <div className="item-footer">
                            <span className="item-price">{item.price.toFixed(2)}€</span>
                            <motion.button
                              className="add-btn"
                              onClick={() => {
                                setSelectedItem(item);
                                setSelectedOptions([]);
                                setSelectedOptionGroups({});
                              }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              Ajouter
                            </motion.button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {selectedItem && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedItem(null)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <button className="modal-close" onClick={() => setSelectedItem(null)}>
                <FaTimes />
              </button>

              <div className="modal-header">
                <div className="modal-image">
                  <img src={selectedItem.image} alt={selectedItem.name} />
                </div>
                <div>
                  <h2>{selectedItem.name}</h2>
                  <p>{selectedItem.description}</p>
                </div>
              </div>



              {/* Groupes d'options */}
              {selectedItem.optionGroups && selectedItem.optionGroups.length > 0 && (
                <>
                  {selectedItem.optionGroups.map(groupId => {
                    const group = optionGroups.find(g => g.id === groupId);
                    if (!group) return null;
                    
                    const selectedOptionsForGroup = selectedOptionGroups[groupId] || [];
                    
                    return (
                      <div key={groupId} className="options-section">
                        <h3>
                          {group.name}
                          {group.required && <span className="required-indicator"> *</span>}
                        </h3>
                        <p className="option-group-description">
                          {group.type === 'single' ? 'Choisissez une option' : 'Choisissez une ou plusieurs options'}
                        </p>
                        <div className={group.type === 'single' ? 'base-options' : 'supplements-grid'}>
                          {group.options.map(option => (
                            <label key={option.id} className={group.type === 'single' ? 'base-option' : 'supplement-option'}>
                              <input
                                type={group.type === 'single' ? 'radio' : 'checkbox'}
                                name={group.type === 'single' ? `group-${groupId}` : undefined}
                                checked={selectedOptionsForGroup.includes(option.id)}
                                onChange={() => handleOptionGroupSelection(groupId, option.id, group.type)}
                              />
                              <span>
                                {option.name}
                                {option.price > 0 && ` (+${option.price.toFixed(2)}€)`}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {selectedItem.allowSupplements && selectedItem.availableSupplements && selectedItem.availableSupplements.length > 0 && (
                <div className="options-section">
                  <h3>Suppléments</h3>
                  <div className="supplements-grid">
                    {supplements
                      .filter(sup => selectedItem.availableSupplements?.includes(sup.id))
                      .map(sup => (
                        <label key={sup.id} className="supplement-option">
                          <input
                            type="checkbox"
                            checked={!!selectedOptions.find(o => o.id === sup.id)}
                            onChange={() => toggleOption(sup)}
                          />
                          <span>{sup.name} (+{sup.price.toFixed(2)}€)</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}

              <div className="modal-footer">
                <div className="modal-total">
                  Total: <strong>{getTotalPrice().toFixed(2)}€</strong>
                </div>
                <motion.button
                  className="btn-primary"
                  onClick={handleAddToCart}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Ajouter au Panier
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCartModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCartModal(false)}
            style={{ zIndex: 2000 }}
          >
            <motion.div
              className="modal-content cart-modal"
              initial={{ scale: 0.5, opacity: 0, y: 100 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 100 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="modal-close"
                onClick={() => setShowCartModal(false)}
              >
                <FaTimes />
              </button>

              <div className="cart-modal-body">
                <div className="success-icon">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.2, type: "spring" }}
                  >
                    <FaStar className="star-icon" />
                  </motion.div>
                </div>
                <h3>Ajouté au panier !</h3>
                {lastAddedItem && (
                  <p className="added-item-name">
                    {lastAddedItem.name} a été ajouté à votre commande.
                  </p>
                )}

                <div className="cart-modal-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => setShowCartModal(false)}
                  >
                    Continuer mes achats
                  </button>
                  <button
                    className="btn-primary"
                    onClick={() => navigate('/order')}
                  >
                    Voir mon panier <FaArrowRight />
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="features">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -10 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <motion.div
          className="cta-content"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Prêt à commander ?</h2>
          <p>Découvrez notre menu complet et passez votre commande en quelques clics</p>
          <motion.button
            className="btn-primary btn-discover"
            onClick={scrollToMenu}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Découvrir le Menu
          </motion.button>
        </motion.div>
      </section>
    </div>
  );
};

export default Home;
